import { useQueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { createContext, PropsWithChildren, useContext } from 'react';
import { useUpdateEffect } from 'react-use';

import { userClient } from '@portal-clients/user';

import { useClientQuery } from '@frontend/hooks';

import { useUser } from './user-context';

export interface AccountTokenContextType {
  tokens: Record<string, string> | undefined;
  refetch(): void;
}

type AccountTokens = Record<string, string>;

/** Refresh this long before a token's `exp` claim. */
const REFRESH_MARGIN_MS = 30_000;

/**
 * Shortest time between two refetches, used when a token already looks
 * expired. Interval refetches never stack: TanStack Query skips the tick while
 * a fetch is in flight, and while the app is in the background.
 */
const MIN_REFETCH_DELAY_MS = 10_000;

const tokenExpiryMs = (token: string): number | undefined => {
  const { exp } = jwtDecode(token);
  return exp === undefined ? undefined : exp * 1000;
};

/**
 * Milliseconds until the tokens should be refetched: shortly before the first
 * of them expires. `false` when there are no tokens to keep fresh.
 */
const nextTokenRefetchDelay = (
  tokens: AccountTokens | undefined,
  now = Date.now(),
): number | false => {
  const expiries = Object.values(tokens ?? {})
    .filter(Boolean)
    .map(tokenExpiryMs)
    .filter((expiry): expiry is number => expiry !== undefined);

  if (expiries.length === 0) {
    return false;
  }

  return Math.max(
    Math.min(...expiries) - REFRESH_MARGIN_MS - now,
    MIN_REFETCH_DELAY_MS,
  );
};

export const AccountTokenContext = createContext<AccountTokenContextType>(
  null!,
);

export const AccountTokenProvider = (props: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const { user } = useUser();

  const { data: tokens, refetch } = useClientQuery<AccountTokens>({
    queryKey: ['tokens'],
    enabled: !!user,
    refetchInterval: (query) => nextTokenRefetchDelay(query.state.data),
    async onFetch({ signal }) {
      if (!user?.accounts.length) {
        return {};
      }

      return Promise.all(
        user.accounts.map((account) =>
          userClient
            .getToken(account.id, { signal })
            .then((token) => ({ [account.id]: token.token })),
        ),
      ).then((tokensArray) =>
        tokensArray.reduce((a, c) => ({ ...a, ...c }), {}),
      );
    },
  });

  useUpdateEffect(() => {
    refetch();
  }, [user?.accounts.map((account) => account.id).join(',')]);

  return (
    <AccountTokenContext.Provider
      value={{
        tokens,
        refetch: () => queryClient.invalidateQueries({ queryKey: ['tokens'] }),
      }}
    >
      {props.children}
    </AccountTokenContext.Provider>
  );
};

export const useAccountToken = (): AccountTokenContextType => {
  const context = useContext(AccountTokenContext);

  if (!context) {
    throw new Error(
      'useAccountToken must be used within a AccountTokenProvider',
    );
  }

  return context;
};

export const useOptionalAccountToken = ():
  | AccountTokenContextType
  | undefined => {
  const context = useContext(AccountTokenContext);
  return context ?? undefined;
};
